import { useCallback, useMemo, useState } from 'react';
import type { TableSortConfig } from 'components/organisms/table';

export type SortValue = string | number | null;

export type SortAccessors<T> = Partial<
  Record<string, (row: T) => SortValue | boolean | undefined>
>;

// Matches ISO-like date strings coming from the API ("2024-05-01", "2024-05-01T10:00:00")
const isoDatePattern = /^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const normalizeSortValue = (value: unknown): SortValue => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return isNaN(value) ? null : value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const text = String(value).trim();
  if (text === '') return null;
  if (isoDatePattern.test(text)) {
    const time = new Date(text).getTime();
    if (!isNaN(time)) return time;
  }
  return text.toLowerCase();
};

const compareSortValues = (a: SortValue, b: SortValue, modifier: number) => {
  // Empty values always go to the bottom regardless of direction
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * modifier;
  return (
    String(a).localeCompare(String(b), undefined, { numeric: true }) * modifier
  );
};

export const sortTableRows = <T,>(
  rows: T[],
  { key, direction }: TableSortConfig,
  accessors: SortAccessors<T> = {},
): T[] => {
  const modifier = direction === 'asc' ? 1 : -1;
  const accessor = accessors[key];
  const getValue = (row: T) =>
    normalizeSortValue(accessor ? accessor(row) : (row as any)?.[key]);
  return [...rows].sort((a, b) =>
    compareSortValues(getValue(a), getValue(b), modifier),
  );
};

/**
 * Click-to-sort state for TableComponent headers.
 * First click on a column sorts ascending, the next click reverses it.
 * Until a header is clicked the rows keep their original (API / page) order.
 *
 * `accessors` maps a column key to the value to sort by, for columns whose key
 * doesn't match a row field or whose displayed value is derived.
 */
export const useTableSort = <T,>(
  rows: T[] | undefined | null,
  accessors?: SortAccessors<T>,
) => {
  const [sortConfig, setSortConfig] = useState<TableSortConfig | null>(null);

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );
  }, []);

  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return sortConfig ? sortTableRows(list, sortConfig, accessors) : list;
    // accessors is expected to be a stable object defined outside the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortConfig]);

  return { sortedRows, sortConfig, handleSort, setSortConfig };
};
